'use client';

import { useTranslations } from 'next-intl';
import type { RelatedNews } from '@/lib/api/event/models';
import { RelativeDateText } from '@/lib/components/Text/RelativeDateText';
import { Link } from '@/lib/routing';
import { Icon } from '@/lib/ui-kit/icons';
import { UiImage } from '@/lib/ui-kit/ui-image';

interface RelatedNewsRowProps {
  news: RelatedNews;
}

export function RelatedNewsRow({ news }: RelatedNewsRowProps) {
  const tNews = useTranslations('news');
  const tMarket = useTranslations('market');
  const isHot = news.tags.some((tag) => tag.toLowerCase().includes('hot'));

  return (
    <Link
      href={`/news/${news.category?.slug?.toLowerCase() || 'general'}/${news.slug}`}
      className="group block rounded-[10px] py-2 transition-colors hover:bg-v2-surface-secondary"
      target="_blank"
      rel="noopener noreferrer"
      data-nav-source="related"
    >
      <div className="flex items-start gap-4">
        <div className="size-[44px] shrink-0 overflow-hidden rounded-[8px] bg-v2-surface-secondary">
          {news.imageLink && (
            <UiImage
              src={news.imageLink}
              alt={news.title}
              size={44}
              className="size-full object-cover"
            />
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex flex-wrap items-center gap-4">
            {isHot && (
              <span className="inline-flex shrink-0 items-center gap-1 overflow-clip rounded-full bg-v2-bg-blue/10 py-1 pl-[7px] pr-2">
                <Icon name="Fire" size={16} className="text-v2-bg-blue" />
                <span className="whitespace-nowrap text-[12px] font-normal leading-4 tracking-[-0.12px] text-v2-text-primary">
                  {tMarket('hottest')}
                </span>
              </span>
            )}
            <RelativeDateText
              date={new Date(news.publishedAt)}
              variant="label-md"
              className="whitespace-nowrap text-[13px] font-normal leading-4 tracking-[-0.13px] text-v2-text-secondary"
            />
            {news.commentCount > 0 && (
              <span className="whitespace-nowrap text-[13px] font-normal leading-4 tracking-[-0.13px] text-v2-text-secondary">
                {tNews('comments', { count: news.commentCount })}
              </span>
            )}
          </div>
          <p className="line-clamp-2 text-[13px] font-normal leading-4 tracking-[-0.13px] text-v2-text-primary transition-colors group-hover:text-v2-text-primary/80">
            {news.title}
          </p>
        </div>
      </div>
    </Link>
  );
}
